import React, { useEffect } from 'react';
import { ArrowRight, Sprout, Droplets, ShieldAlert, Award, ChevronRight } from 'lucide-react';
import { FeatureProfile, FEATURE_PROFILES } from '../data/featureProfiles';
import { PRODUCTS } from '../constants';

interface FeatureProductPageProps {
  profile: FeatureProfile;
  onShopClick: () => void;
  onWholesaleClick: () => void;
  onViewProfile: (slug: string) => void;
}

const FeatureProductPage: React.FC<FeatureProductPageProps> = ({
  profile,
  onShopClick,
  onWholesaleClick,
  onViewProfile,
}) => {
  // New profile = new page: start at the top.
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [profile.slug]);

  const variants = profile.productIds
    .map((id) => PRODUCTS.find((p) => p.id === id))
    .filter((p): p is NonNullable<typeof p> => Boolean(p));
  const primary = variants[0];
  const sizes = variants.map((v) => `${v.bladeLength}"`).join(' · ');
  const others = FEATURE_PROFILES.filter((p) => p.slug !== profile.slug);

  return (
    <div className="bg-paper">
      {/* Hero */}
      <section className="bg-paper-bright border-b border-ink-200 py-20 lg:py-28 px-6 lg:px-10">
        <div className="max-w-editorial mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="md:col-span-6">
            <p className="font-mono text-[11px] tracking-micro uppercase text-ember mb-5">
              Featured profile — {profile.label}
            </p>
            <h1 className="font-editorial italic font-light text-5xl lg:text-6xl text-ink-950 leading-[1.05] tracking-tight mb-6">
              {profile.name}.
            </h1>
            <p className="text-ink-900 text-xl leading-[1.5] mb-6 max-w-prose">{profile.tagline}</p>
            <div className="space-y-4 max-w-prose mb-10">
              {profile.intro.map((para) => (
                <p key={para.slice(0, 32)} className="text-ink-700 leading-[1.65]">
                  {para}
                </p>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-x-10 gap-y-4 border-t border-ink-200 pt-6 mb-10">
              {primary && (
                <div>
                  <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-1">Retail</p>
                  <p className="font-editorial italic text-2xl text-ink-950">
                    ${(primary.retailPrice ?? primary.msrp).toFixed(2)} <span className="text-sm not-italic font-sans text-ink-500">CAD</span>
                  </p>
                </div>
              )}
              <div>
                <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-1">Blade length</p>
                <p className="font-editorial italic text-2xl text-ink-950">{sizes}</p>
              </div>
              {primary && (
                <div>
                  <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-1">Steel</p>
                  <p className="font-editorial italic text-2xl text-ink-950">{primary.specs.steel}</p>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-4">
              <button
                onClick={onShopClick}
                className="group inline-flex items-center gap-2.5 bg-ink-950 text-paper-warm px-7 py-3.5 text-sm font-medium hover:bg-ember transition-colors duration-200"
              >
                Shop this blade
                <ArrowRight className="w-4 h-4 transition-transform duration-200 ease-editorial group-hover:translate-x-0.5" strokeWidth={1.5} />
              </button>
              <button
                onClick={onWholesaleClick}
                className="inline-flex items-center gap-2.5 border border-ink-300 text-ink-900 px-7 py-3.5 text-sm font-medium hover:border-ember hover:text-ember transition-colors duration-200"
              >
                Wholesale enquiries
              </button>
            </div>
          </div>

          <div className="md:col-span-6">
            <div className="aspect-[4/3] bg-paper border border-ink-200 flex items-center justify-center p-4">
              <img
                src={profile.image}
                alt={`${profile.name} — product photograph`}
                className="max-w-full max-h-full object-contain mix-blend-multiply"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className="py-20 lg:py-28 px-6 lg:px-10">
        <div className="max-w-editorial mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <Sprout className="w-4 h-4 text-ember" strokeWidth={1.5} />
            <p className="font-mono text-[11px] tracking-micro uppercase text-ember">Best uses</p>
          </div>
          <h2 className="font-editorial italic font-light text-4xl lg:text-5xl text-ink-950 tracking-tight mb-12 lg:mb-16">
            Where it earns its keep.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
            {profile.useCases.map((item) => (
              <article key={item.title} className="border-l-2 border-ink-200 hover:border-ember pl-6 transition-colors duration-300">
                <h3 className="font-editorial italic text-2xl text-ink-950 tracking-tight mb-3">{item.title}.</h3>
                <p className="text-sm text-ink-700 leading-relaxed max-w-prose">{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Care */}
      <section className="bg-paper-bright border-t border-b border-ink-100 py-20 lg:py-28 px-6 lg:px-10">
        <div className="max-w-editorial mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <Droplets className="w-4 h-4 text-ember" strokeWidth={1.5} />
            <p className="font-mono text-[11px] tracking-micro uppercase text-ember">Care &amp; maintenance</p>
          </div>
          <h2 className="font-editorial italic font-light text-4xl lg:text-5xl text-ink-950 tracking-tight mb-12 lg:mb-16">
            Keep it working for decades.
          </h2>
          <div className="space-y-10 max-w-3xl">
            {profile.care.map((item, i) => (
              <article key={item.title} className="flex gap-6">
                <span className="font-mono text-[11px] text-ember pt-1.5 flex-shrink-0 w-8">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div>
                  <h3 className="font-editorial italic text-2xl text-ink-950 tracking-tight mb-2">{item.title}.</h3>
                  <p className="text-sm text-ink-700 leading-relaxed">{item.body}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Safety */}
      <section className="py-20 lg:py-28 px-6 lg:px-10">
        <div className="max-w-editorial mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <ShieldAlert className="w-4 h-4 text-ember" strokeWidth={1.5} />
            <p className="font-mono text-[11px] tracking-micro uppercase text-ember">Safe handling</p>
          </div>
          <h2 className="font-editorial italic font-light text-4xl lg:text-5xl text-ink-950 tracking-tight mb-6">
            Use it like a professional.
          </h2>
          <p className="text-ink-700 max-w-prose mb-12 lg:mb-16 leading-relaxed">
            A machete is an 18+ working tool, and we verify photo ID on every delivery. These habits are how
            field crews use the same blade for a full career without incident.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {profile.safety.map((item) => (
              <article key={item.title} className="bg-paper-bright border border-ink-200 p-7">
                <h3 className="font-editorial italic text-xl text-ink-950 tracking-tight mb-3">{item.title}.</h3>
                <p className="text-sm text-ink-700 leading-relaxed">{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Incolma quality — shared distributor section */}
      <section className="bg-ink-950 text-paper-warm py-20 lg:py-28 px-6 lg:px-10">
        <div className="max-w-editorial mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-16">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3 mb-4">
              <Award className="w-4 h-4 text-ember" strokeWidth={1.5} />
              <p className="font-mono text-[11px] tracking-micro uppercase text-ember">The Incolma standard</p>
            </div>
            <h2 className="font-editorial italic font-light text-4xl lg:text-5xl tracking-tight mb-8 text-paper-warm">
              Two centuries of steel behind every blade.
            </h2>
            <div className="space-y-5 max-w-prose text-paper-warm/80 leading-[1.65]">
              <p>
                Incolma's bladesmithing lineage runs back to Gebr. Weyersberg of Solingen, Germany — a forge
                house founded in 1787 in the city that defined European edge steel. When post-war trade made
                German imports impossible, Weyersberg's patents, processes, and brands crossed the Atlantic, and
                on May 20, 1960, Incolma was founded in Manizales, Colombia, in the heart of the coffee highlands.
              </p>
              <p>
                Today that forge is the world's largest machete factory — drop-forging high-carbon steel blades
                by the hundreds of thousands every month, in hundreds of patterns, shipped to working farms in
                more than 55 countries. Every blade is hardened and tempered to a 52–58 HRC working hardness —
                hard enough to hold an edge through a full day, tough enough to flex instead of chip — with
                full-tang construction as standard.
              </p>
              <p>
                The Machete Man is Canada's exclusive Incolma distributor. Every blade we sell ships
                factory-direct from Manizales to our Scarborough, Ontario warehouse — no grey-market imports, no
                lookalikes — and is backed by our manufacturing-defect warranty.
              </p>
            </div>
          </div>
          <div className="md:col-span-5 md:pt-24">
            <dl className="border-t border-paper-warm/15">
              {[
                ['Forged in', 'Manizales, Colombia'],
                ['Est.', 'May 20, 1960'],
                ['Heritage', 'Gebr. Weyersberg, Solingen · 1787'],
                ['Steel', 'Drop-forged high-carbon (SAE 1075)'],
                ['Hardness', '52–58 HRC, hardened & tempered'],
                ['Construction', 'Full tang'],
                ['Distribution', 'Exclusive to Canada via The Machete Man'],
              ].map(([term, def]) => (
                <div key={term} className="flex justify-between gap-6 py-4 border-b border-paper-warm/15">
                  <dt className="font-mono text-[10px] tracking-micro uppercase text-paper-warm/50 pt-0.5 flex-shrink-0">{term}</dt>
                  <dd className="text-sm text-paper-warm text-right">{def}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      {/* Other profiles + CTA */}
      <section className="bg-paper-bright border-b border-ink-200 py-20 lg:py-24 px-6 lg:px-10">
        <div className="max-w-editorial mx-auto">
          <p className="font-mono text-[11px] tracking-micro uppercase text-ember mb-4">More profiles</p>
          <h2 className="font-editorial italic font-light text-3xl lg:text-4xl text-ink-950 tracking-tight mb-10">
            The other best-sellers.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-14">
            {others.map((p) => (
              <button
                key={p.slug}
                onClick={() => onViewProfile(p.slug)}
                className="group text-left flex items-center gap-6 border border-ink-200 hover:border-ember bg-paper p-5 transition-colors duration-200"
              >
                <div className="w-24 h-20 flex-shrink-0 flex items-center justify-center p-1">
                  <img src={p.image} alt={p.name} className="max-w-full max-h-full object-contain mix-blend-multiply" />
                </div>
                <div className="flex-1">
                  <p className="font-mono text-[10px] tracking-micro uppercase text-ember mb-1">{p.label}</p>
                  <p className="font-editorial italic text-xl text-ink-950 tracking-tight">{p.name}.</p>
                </div>
                <ChevronRight className="w-4 h-4 text-ink-300 group-hover:text-ember transition-colors duration-200 flex-shrink-0" strokeWidth={1.5} />
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-6 border-t border-ink-200 pt-10">
            <p className="font-editorial italic text-2xl text-ink-950 tracking-tight">
              Ready to put one to work?
            </p>
            <button
              onClick={onShopClick}
              className="group inline-flex items-center gap-2.5 bg-ink-950 text-paper-warm px-7 py-3.5 text-sm font-medium hover:bg-ember transition-colors duration-200"
            >
              Browse the full catalog
              <ArrowRight className="w-4 h-4 transition-transform duration-200 ease-editorial group-hover:translate-x-0.5" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FeatureProductPage;
